/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * FIX: DESKTOP MEGA MENU OVERLAP (All In Advertising, September 2026)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Stock Focal 10.0.1 keeps a mega menu at full opacity for 250ms after the pointer moves to another top-level
 * item, then fades it out for another 250ms while the next menu is already fading in. Moving across several
 * items stacks two or three menus on top of each other, which is very visible with this store's large menus.
 *
 * The desktop-navigation element is patched here rather than in theme.js, so theme.js stays identical to the
 * Focal release and survives theme updates. This patch:
 * - hides sibling dropdowns instantly as soon as another one opens, and shows the new one without a fade when
 *   it replaces a visible menu, so exactly one menu is on screen at a time;
 * - cancels a pending hide when a menu is reopened, so a stale timer can no longer hide the menu under the pointer;
 * - ignores menu items without a dropdown element (a mega menu block with no links or images, such as
 *   "All Tags"), which previously threw a TypeError on every hover.
 */
customElements.whenDefined('desktop-navigation').then(() => {
  const DesktopNavigation = customElements.get('desktop-navigation');
  const hideTimers = new WeakMap();

  const style = document.createElement('style');
  style.textContent = '.mega-menu.is-instant, .mega-menu.is-instant::after, .nav-dropdown.is-instant { transition: none !important; }';
  document.head.appendChild(style);

  const getDropdown = (item) => {
    const link = item.querySelector('[aria-controls]');
    return link ? item.querySelector(`#${CSS.escape(link.getAttribute('aria-controls'))}`) : null;
  };

  // Applies a visibility change with transitions disabled, then restores them once the change is committed.
  const withoutTransition = (dropdown, change) => {
    dropdown.classList.add('is-instant');
    change();
    void dropdown.offsetHeight;
    dropdown.classList.remove('is-instant');
  };

  const onDropdownOpened = (link) => {
    const item = link.parentElement;
    const dropdown = getDropdown(item);
    if (!dropdown) return;

    clearTimeout(hideTimers.get(dropdown));
    let replacedVisibleMenu = false;

    Array.from(item.parentElement.children).forEach((sibling) => {
      if (sibling === item || !sibling.classList.contains('has-dropdown')) return;
      const siblingDropdown = getDropdown(sibling);
      if (!siblingDropdown) return;

      clearTimeout(hideTimers.get(siblingDropdown));
      const siblingLink = sibling.querySelector('[aria-controls]');
      if (siblingLink.getAttribute('aria-expanded') === 'true') {
        siblingLink.setAttribute('aria-expanded', 'false');
      }

      if (getComputedStyle(siblingDropdown).visibility === 'visible') {
        replacedVisibleMenu = true;
        withoutTransition(siblingDropdown, () => {
          siblingDropdown.setAttribute('hidden', '');
          siblingDropdown.classList.remove('is-closing');
        });
      }
    });

    if (replacedVisibleMenu) {
      withoutTransition(dropdown, () => dropdown.classList.remove('is-closing'));
    } else {
      dropdown.classList.remove('is-closing');
    }
  };

  const stockOpenDropdown = DesktopNavigation.prototype.openDropdown;
  DesktopNavigation.prototype.openDropdown = function (parentElement) {
    if (!getDropdown(parentElement)) return;
    stockOpenDropdown.call(this, parentElement);
  };

  // Same as the stock method, except the hide timer is tracked per dropdown so it can be cancelled.
  DesktopNavigation.prototype.closeDropdown = function (parentElement) {
    const link = parentElement.querySelector('[aria-controls]');
    const dropdown = getDropdown(parentElement);
    if (!dropdown) return;

    requestAnimationFrame(() => {
      dropdown.classList.add('is-closing');
      link.setAttribute('aria-expanded', 'false');
      clearTimeout(hideTimers.get(dropdown));

      const delay = dropdown.classList.contains('mega-menu') && this.currentMegaMenu !== dropdown ? 250 : 0;
      hideTimers.set(dropdown, setTimeout(() => {
        hideTimers.delete(dropdown);
        dropdown.classList.remove('is-closing');
        // The pointer came back before the delay ran out: keep the reopened menu on screen.
        if (link.getAttribute('aria-expanded') !== 'true') {
          dropdown.setAttribute('hidden', '');
        }
      }, delay));

      this.dispatchEvent(new CustomEvent('desktop-nav:dropdown:close', { bubbles: true }));
    });
  };

  // Every open path (hover, touch click, theme editor block select) sets aria-expanded="true" on the link.
  const observed = new WeakSet();
  const observe = (navigation) => {
    if (observed.has(navigation)) return;
    observed.add(navigation);
    new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.target.getAttribute('aria-expanded') === 'true') onDropdownOpened(record.target);
      });
    }).observe(navigation, { attributes: true, attributeFilter: ['aria-expanded'], subtree: true });
  };

  document.querySelectorAll('desktop-navigation').forEach(observe);
  // The theme editor re-renders the header section, which creates a new desktop-navigation element.
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('desktop-navigation').forEach(observe);
  });
});
